import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseChart, Retry } from '@components/common';
import { chartOption } from '../utils';
import { useFetchApiUsageBreakdownTimeseries } from './hooks';
import { useMakeExportData, useGetPlanLimitForMetric } from '../hooks/useMetricSeries';
import { intervalAtom, usageMetricList } from '../atoms';
import { useAtomValue } from 'jotai';
import {
  HeaderWrapper,
  TitleWrapper,
  SecondRow,
  ValueWrapper,
} from '@components/metrics-panel-hz/MetricCard/styled';
import {
  buildSeriesData,
  generateMarkLine,
  getUsageMetricsMeta,
  isNoPlanLimitMetric,
  isPlanLimitZero,
} from '../hooks/utils';
import { humanize } from '@utils';
import { Divider } from 'antd';
import { cloneDeep } from 'lodash';

const cardStyle = {
  height: '100%',
  backgroundColor: 'white',
  borderRadius: 6,
  borderStyle: 'solid',
  borderWidth: 'thin',
  borderColor: '#dedede',
};

const DISPLAY_NAME_OVERRIDES = {
  HistoricalAPICalls: 'Analytics API calls',
  RealtimeAPICalls: 'Real-Time API calls',
};

interface BreakdownChartCardProps {
  metric: string;
  seriesData: any;
  isFetching: boolean;
  isError: boolean;
}

export const BreakdownChartCard = ({
  metric,
  seriesData,
  isFetching,
  isError,
}: BreakdownChartCardProps) => {
  const [instance, setInstance] = useState(null);
  const interval = useAtomValue(intervalAtom);
  const metricChartOption = useMemo(() => chartOption(metric), [metric]);

  const chartInstanceRefCb = useCallback(
    (inst) => {
      setInstance(inst);
      inst.setOption(metricChartOption);
    },
    [metricChartOption],
  );

  const chartData = seriesData?.data?.[metric] || [];
  const total = seriesData?.totals?.[metric];
  const displayName =
    DISPLAY_NAME_OVERRIDES[metric] || usageMetricList[metric]?.displayName || metric;

  const { data: planLimitData }: any = useGetPlanLimitForMetric(metric);
  const markLine =
    isNoPlanLimitMetric(metric) || isPlanLimitZero(planLimitData)
      ? {}
      : generateMarkLine(planLimitData?.data, metric, chartData);

  useEffect(() => {
    if (instance && chartData.length > 0) {
      buildSeriesData({
        instance,
        data: chartData,
        allMetrics: cloneDeep(getUsageMetricsMeta()),
        metric,
        markLine: markLine || {},
        interval,
      });
    }
  }, [instance, chartData, metric, planLimitData, interval]);

  const usageMetricsMeta = getUsageMetricsMeta();
  const showLimitLabel =
    !isNoPlanLimitMetric(metric) && !isPlanLimitZero(planLimitData) && total != null;
  const limitText = 'Daily Limit';
  const displayLimit = (value) => {
    const result = humanize(value, usageMetricsMeta[metric].unit);
    return result != null ? `${result} ${usageMetricList[metric].localUnit || ''}`.trim() : '';
  };

  return (
    <div style={cardStyle}>
      <HeaderWrapper>
        <TitleWrapper>
          <h4 style={{ marginBottom: 0 }}>{displayName}</h4>
        </TitleWrapper>
        <SecondRow>
          <ValueWrapper>{total != null ? humanize(total) : ''}</ValueWrapper>
          {showLimitLabel ? (
            <>
              <Divider type="vertical" style={{ height: 24 }} />
              <ValueWrapper style={{ whiteSpace: 'pre-wrap' }}>
                <Divider
                  style={{
                    borderColor: '#FF4D4F',
                    borderWidth: 1,
                    margin: 0,
                    minWidth: 24,
                    width: 24,
                  }}
                  variant="dashed"
                  dashed
                />
                <span> {limitText} </span>
                <span>{displayLimit(planLimitData?.total)}</span>
              </ValueWrapper>
            </>
          ) : null}
        </SecondRow>
      </HeaderWrapper>
      <Retry isLoading={isFetching} showError={isError} progress={true}>
        <BaseChart
          key={metric}
          chartId={`breakdown-${metric}`}
          chartInstanceRefCb={chartInstanceRefCb}
          style={{ height: '150px', marginTop: '5px' }}
        />
      </Retry>
    </div>
  );
};

export const useBreakdownChartData = () => {
  const { data: seriesData, isFetching, isError } = useFetchApiUsageBreakdownTimeseries();
  useMakeExportData({
    seriesData,
    sectionKey: 'ud-api-breakdown-timeseries',
    title: 'API Usage Breakdown',
  });
  return { seriesData, isFetching, isError };
};
